import React, { useId, useState } from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import { Link } from '../../../../components/Link';
import { Msg, useFormatMessage } from '../../../../i18n-review/client/Msg';
import { netFromGrid } from '../../../../utils/salary/net-pay-grid';
import { percentileOf } from '../../../../utils/salary/salary-role-insights';
import { Card } from '../../../../design-system/ui/Card/Card';
import { FilterChip } from '../../../../design-system/ui/FilterChip/FilterChip';
import { IconButton } from '../../../../design-system/ui/IconButton/IconButton';
import { Input } from '../../../../design-system/ui/Input/Input';
import { MarketBar } from '../../../../design-system/ui/MarketBar/MarketBar';
import { TrendChip } from '../../../../design-system/ui/TrendChip/TrendChip';
import { TemplateSection } from '../../../../design-system/ui/templates/Section';
import type { Data } from '../+data.server';
import { v2Messages as m } from './salary-role-v2.messages';
import { cleanLocation, eur, eurRange } from './SalaryRoleSections';

type Loc = 'en' | 'fi' | 'sv';
type Sector = 'private' | 'public' | 'agency' | 'unknown';

const STEP = 50;

/**
 * "Am I paid fairly?": the visitor types their base pay and gets, from the same numbers the page
 * already shows, where it sits among the ads, what it leaves after tax, what the other sector
 * typically advertises, and which open jobs beat it. Runs entirely in the browser (the page is a
 * hydrated island); nothing is sent or stored, which the copy says up front.
 */
export function PayCheck({
  data,
  locale,
  medianMid,
  jobsHref,
}: {
  data: Data;
  locale: Loc;
  medianMid: number;
  jobsHref: string | null;
}) {
  const formatMessage = useFormatMessage();
  const inputId = useId();
  const groupId = useId();
  const stats = data.overallStats!;
  const [pay, setPay] = useState<number>(Math.round(medianMid / STEP) * STEP);
  const [raw, setRaw] = useState<string>(String(Math.round(medianMid / STEP) * STEP));
  const [sector, setSector] = useState<Sector>('unknown');

  const p10 = stats.min ?? null;
  const p25 = stats.percentile25?.min ?? null;
  const p75 = stats.percentile75?.max ?? null;
  const p90 = stats.percentile90?.max ?? null;
  const points: Array<[number, number]> = [
    ...(p10 ? [[p10, 10] as [number, number]] : []),
    ...(p25 ? [[p25, 25] as [number, number]] : []),
    [medianMid, 50],
    ...(p75 ? [[p75, 75] as [number, number]] : []),
    ...(p90 ? [[p90, 90] as [number, number]] : []),
  ];
  const pct = percentileOf(pay, points);
  const lo = Math.min(p10 ?? medianMid * 0.8, pay) * 0.92;
  const hi = Math.max(p90 ?? medianMid * 1.2, pay) * 1.06;
  const x = (v: number) => `${Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100)).toFixed(1)}%`;
  const net = netFromGrid(data.netPayGrid, pay);

  const sectors = data.market?.summary.sectors ?? [];
  const publicPay = sectors.find((s) => s.sector === 'public')?.median ?? null;
  const privatePay = sectors.find((s) => s.sector === 'private')?.median ?? null;
  const other: { sector: 'public' | 'private'; pay: number } | null =
    sector === 'private' && publicPay
      ? { sector: 'public', pay: publicPay }
      : sector === 'public' && privatePay
        ? { sector: 'private', pay: privatePay }
        : sector === 'agency' && (publicPay || privatePay)
          ? (publicPay ?? 0) >= (privatePay ?? 0)
            ? { sector: 'public', pay: publicPay! }
            : { sector: 'private', pay: privatePay! }
          : null;

  const better = (data.market?.topPaying ?? []).filter((j) => (j.salaryMax ?? j.salaryMin ?? 0) > pay);

  const commit = (value: number) => {
    const v = Math.max(500, Math.min(20000, Math.round(value)));
    setPay(v);
    setRaw(String(v));
  };

  const headline = pct <= 10 ? m.payCheckBottom : pct >= 90 ? m.payCheckTop : m.payCheckPct;
  const sectorOptions: Array<{ key: Sector; label: typeof m.sectorPrivate }> = [
    { key: 'private', label: m.sectorPrivate },
    { key: 'public', label: m.sectorPublic },
    { key: 'agency', label: m.sectorAgency },
    { key: 'unknown', label: m.sectorUnknown },
  ];

  const sampleCount = stats.sampleSize ?? stats.count ?? 0;
  const youDescription = formatMessage(m.payCheckMoreThan, { share: 100 - pct, pay: eur(pay, locale) });

  return (
    <TemplateSection
      id="pay-check"
      title={<Msg {...m.payCheckTitle} />}
      description={<Msg {...m.payCheckSub} values={{ count: sampleCount }} />}
    >
      <div className="grid items-start gap-6 lg:grid-cols-5">
        <div className="flex flex-col gap-5 lg:col-span-2">
          <div className="flex flex-col gap-2">
            <div className="flex items-end gap-2">
              <IconButton
                icon={Minus}
                label={formatMessage(m.payCheckDecrease)}
                onClick={() => commit(pay - STEP)}
                className="shrink-0"
              />
              <Input
                id={inputId}
                label={<Msg {...m.payCheckInputLabel} />}
                fieldClassName="min-w-0 flex-1"
                inputMode="numeric"
                autoComplete="off"
                value={raw}
                onChange={(e) => {
                  setRaw(e.target.value);
                  const v = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10);
                  if (Number.isFinite(v) && v >= 500 && v <= 20000) setPay(v);
                }}
                onBlur={() => commit(pay)}
              />
              <IconButton
                icon={Plus}
                label={formatMessage(m.payCheckIncrease)}
                onClick={() => commit(pay + STEP)}
                className="shrink-0"
              />
            </div>
            <p className="type-caption text-jc-ink-secondary">
              <Msg {...m.payCheckInputHint} />
            </p>
          </div>
          {publicPay || privatePay ? (
            <div className="flex flex-col gap-2">
              <p id={groupId} className="text-sm font-semibold text-jc-ink">
                <Msg {...m.payCheckEmployerLabel} />
              </p>
              <div role="group" aria-labelledby={groupId} className="flex flex-wrap gap-2">
                {sectorOptions.map((o) => (
                  <FilterChip
                    key={o.key}
                    label={formatMessage(o.label)}
                    selected={sector === o.key}
                    onSelectedChange={() => setSector(o.key)}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-4 lg:col-span-3" aria-live="polite">
          <Card flat padding="md" className="flex flex-col gap-3 bg-jc-page">
            <p className="type-title text-jc-ink">
              <Msg {...headline} values={{ pct }} />
            </p>
            <p className="text-sm text-jc-ink-secondary">
              <Msg {...m.payCheckMoreThan} values={{ share: 100 - pct, pay: eur(pay, locale) }} />
            </p>
            <MarketBar
              variant="expanded"
              barSize="lg"
              min={pay}
              median={medianMid}
              p25={p25 ?? undefined}
              p75={p75 ?? undefined}
              scale={{ min: lo, max: hi }}
              sampleSize={sampleCount}
              thinLabels={{ rough: formatMessage(m.thinRough), tooFew: formatMessage(m.thinTooFew) }}
              p25Label={p25 ? eur(p25, locale) : ''}
              p75Label={p75 ? eur(p75, locale) : ''}
              sample={<Msg {...m.provPostings} values={{ count: sampleCount }} />}
              description={youDescription}
            />
          </Card>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Card flat padding="sm" className="flex flex-col gap-1">
              <p className="type-eyebrow text-jc-ink-secondary">
                <Msg {...m.payCheckNetLabel} />
              </p>
              <p className="type-figure text-jc-ink">{eur(net, locale)}</p>
              <p className="type-caption text-jc-ink-secondary">
                <Msg {...m.payCheckNetNote} />
              </p>
            </Card>
            {other || (sector === 'unknown' && publicPay && privatePay) ? (
              <Card flat padding="sm" className="flex flex-col gap-1">
                {other ? (
                  <>
                    <p className="type-eyebrow text-jc-ink-secondary">
                      <Msg {...m.payCheckMoveTo} values={{ sector: other.sector }} />
                    </p>
                    {other.pay > pay ? (
                      <>
                        <p>
                          <TrendChip direction="up" tone="positive">
                            +{eur(other.pay - pay, locale)}
                          </TrendChip>
                        </p>
                        <p className="type-caption text-jc-ink-secondary">
                          <Msg
                            {...m.payCheckMoveGain}
                            values={{
                              pay: eur(other.pay, locale),
                              net: eur(netFromGrid(data.netPayGrid, other.pay) - net, locale),
                            }}
                          />
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-jc-ink-secondary">
                        <Msg {...m.payCheckMoveNoGain} values={{ pay: eur(other.pay, locale) }} />
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-jc-ink-secondary">
                    <Msg
                      {...m.payCheckSectorGap}
                      values={{ publicPay: eur(publicPay!, locale), privatePay: eur(privatePay!, locale) }}
                    />
                  </p>
                )}
              </Card>
            ) : null}
          </div>

          {data.market && data.market.topPaying.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="type-subheading text-jc-ink">
                  <Msg {...m.payCheckBetterJobs} />
                </h3>
                {jobsHref ? (
                  <a href={jobsHref} className="text-sm font-semibold text-jc-ink underline underline-offset-4">
                    <Msg {...m.payCheckAllJobs} values={{ count: data.market.summary.openCount }} />
                  </a>
                ) : null}
              </div>
              {better.length > 0 ? (
                <ul className="m-0 flex list-none flex-col divide-y divide-jc-border rounded-jc-info bg-jc-surface p-0 ring-1 ring-inset ring-jc-card-border">
                  {better.map((j) => (
                    <li key={j.href}>
                      <Link
                        href={j.href}
                        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 no-underline hover:bg-jc-elevated"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-jc-ink">{j.title}</span>
                          <span className="block truncate type-caption text-jc-ink-secondary">
                            {j.company}
                            {j.location ? ` · ${cleanLocation(j.location)}` : ''}
                          </span>
                        </span>
                        <span className="flex flex-col items-end gap-1">
                          <span className="type-figure-sm text-jc-ink">
                            {j.salaryMin && j.salaryMax
                              ? eurRange(j.salaryMin, j.salaryMax, locale)
                              : eur(j.salaryMax ?? j.salaryMin ?? 0, locale)}
                          </span>
                          <TrendChip direction="up" tone="positive">
                            +{eur((j.salaryMax ?? j.salaryMin ?? 0) - pay, locale)}
                          </TrendChip>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-jc-ink-secondary">
                  <Msg {...m.payCheckNoBetterJobs} />
                </p>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <p className="type-caption text-jc-ink-secondary">
        <Msg {...m.payCheckFootnote} />
      </p>
    </TemplateSection>
  );
}
